import rawAnimationTuning from "./animationTuning.json"

// The single source of truth for every tunable HUD easing/timing value. The
// HUD-animation studio (/hud-studio) edits these live and saves them back to
// animationTuning.json via /api/animation-tuning; the module constants that
// used to hold these numbers now read from here (see graphConstants.ts), so a
// save takes effect app-wide on the next load. In the running studio, a React
// context (animationTuningContext) overrides these for instant preview.
//
// This module is intentionally React-free so it can be imported from both
// server and client code (graphConstants.ts is pulled into server components).

// An easing is stored as a CSS <timing-function> string ("linear", "ease",
// "ease-out", "cubic-bezier(a, b, c, d)") or one of a few named JS curves
// ("easeOutCubic", "easeInOutCubic", "easeInOutSine"). resolveCssEasing/
// resolveJsEasing below turn it into whichever form a consumer needs, so the
// same field drives both CSS transitions and rAF-driven tweens.
export type EasingSpec = string

export type AnimationTuning = {
  // ── Core timeline transition ──
  // The master sync window: the date spring's settle time and the graph
  // viewport spring's duration both derive from it.
  hudSyncTransitionMs: number
  // Critically-damped date spring "snappiness": ω·t at which the step
  // response is considered settled (6.64 ≈ 99%). Higher = quicker settle.
  dateSpringSettleFactor: number
  // Exponential-smoothing factor for the graph camera (animateViewportScale):
  // y-ceiling and future-window each ease toward target by this fraction per
  // frame. Higher = snappier, lower = more gliding.
  viewportSmoothingFactor: number
  // Per-point line-morph easing when the trajectory changes.
  lineMorphEasing: "cubic" | "sine"

  // ── Panel micro-animations ──
  policyMotionDurationMs: number
  policyMotionEasing: EasingSpec
  treemapLayoutMs: number
  treemapLayoutEasing: EasingSpec
  treemapColorMs: number
  treemapColorEasing: EasingSpec
  // How long a departing label takes to fade out as its cell is replaced by the
  // pieces that split off it (the split's walls/moves themselves run over
  // treemapLayoutMs). See useTreemapTransition.
  treemapLabelPhaseMs: number
  metricBarFillDurationMs: number
  metricBarFillEasing: EasingSpec
  metricBarColorMs: number

  // ── Epilogue transition (tuned in its own studio section) ──
  // The end-of-timeline choreography: the Earth drifts to screen centre on
  // its own slower clock (epilogueGlobeMoveMs); the graph shrinks & slides
  // down together with the space-parcel rise (epilogueGraphMoveMs); the rest
  // of the HUD fades out (epilogueHudFadeMs). epilogueMoveEasing is the shared
  // curve for the graph/globe/parcel moves. Distinct from the core timeline
  // transition above — do not conflate the two.
  epilogueGlobeMoveMs: number
  epilogueGraphMoveMs: number
  epilogueHudFadeMs: number
  epilogueMoveEasing: EasingSpec

  // ── Mobile strip (the bottom pill) ──
  // How long the strip's camera animates between two keyframed states (the
  // /zoom-editor mobile-surface keyframes: yMax / startYear / endYear steps
  // at story markers), and its curve.
  mobileZoomTweenMs: number
  mobileZoomTweenEasing: EasingSpec
  // The collapsed-pill ↔ expanded-sheet morph (full drag distance; partial
  // opens/closes take a proportional slice).
  mobileSheetMorphMs: number

  // ── Human-vs-AI labor graph ──
  // The dot field grows as economic value does: laborDotGrowthMs is the
  // count tween (dots appearing), laborDotHeightMs the field-height tween,
  // laborDotEasing the shared curve for both. The legend's counts / ×speed
  // and the AI-noun label resize animate on laborLegendTickerMs +
  // laborLegendTickerEasing.
  laborDotGrowthMs: number
  laborDotEasing: EasingSpec
  laborDotHeightMs: number
  laborLegendTickerMs: number
  laborLegendTickerEasing: EasingSpec
}

export const DEFAULT_ANIMATION_TUNING: AnimationTuning =
  rawAnimationTuning as AnimationTuning

// Fill any missing/invalid fields from the defaults so a partial or stale
// saved payload can't crash a consumer (mirrors sanitizeZoomKeyframes).
export const sanitizeAnimationTuning = (raw: unknown): AnimationTuning => {
  const input = (raw ?? {}) as Partial<AnimationTuning>
  const numberOr = (value: unknown, fallback: number): number =>
    typeof value === "number" && Number.isFinite(value) ? value : fallback
  const stringOr = (value: unknown, fallback: string): string =>
    typeof value === "string" && value.trim() !== "" ? value : fallback
  return {
    hudSyncTransitionMs: numberOr(input.hudSyncTransitionMs, DEFAULT_ANIMATION_TUNING.hudSyncTransitionMs),
    dateSpringSettleFactor: numberOr(input.dateSpringSettleFactor, DEFAULT_ANIMATION_TUNING.dateSpringSettleFactor),
    viewportSmoothingFactor: numberOr(input.viewportSmoothingFactor, DEFAULT_ANIMATION_TUNING.viewportSmoothingFactor),
    lineMorphEasing:
      input.lineMorphEasing === "sine" || input.lineMorphEasing === "cubic"
        ? input.lineMorphEasing
        : DEFAULT_ANIMATION_TUNING.lineMorphEasing,
    policyMotionDurationMs: numberOr(input.policyMotionDurationMs, DEFAULT_ANIMATION_TUNING.policyMotionDurationMs),
    policyMotionEasing: stringOr(input.policyMotionEasing, DEFAULT_ANIMATION_TUNING.policyMotionEasing),
    treemapLayoutMs: numberOr(input.treemapLayoutMs, DEFAULT_ANIMATION_TUNING.treemapLayoutMs),
    treemapLayoutEasing: stringOr(input.treemapLayoutEasing, DEFAULT_ANIMATION_TUNING.treemapLayoutEasing),
    treemapColorMs: numberOr(input.treemapColorMs, DEFAULT_ANIMATION_TUNING.treemapColorMs),
    treemapColorEasing: stringOr(input.treemapColorEasing, DEFAULT_ANIMATION_TUNING.treemapColorEasing),
    treemapLabelPhaseMs: numberOr(input.treemapLabelPhaseMs, DEFAULT_ANIMATION_TUNING.treemapLabelPhaseMs),
    metricBarFillDurationMs: numberOr(input.metricBarFillDurationMs, DEFAULT_ANIMATION_TUNING.metricBarFillDurationMs),
    metricBarFillEasing: stringOr(input.metricBarFillEasing, DEFAULT_ANIMATION_TUNING.metricBarFillEasing),
    metricBarColorMs: numberOr(input.metricBarColorMs, DEFAULT_ANIMATION_TUNING.metricBarColorMs),
    epilogueGlobeMoveMs: numberOr(input.epilogueGlobeMoveMs, DEFAULT_ANIMATION_TUNING.epilogueGlobeMoveMs),
    epilogueGraphMoveMs: numberOr(input.epilogueGraphMoveMs, DEFAULT_ANIMATION_TUNING.epilogueGraphMoveMs),
    epilogueHudFadeMs: numberOr(input.epilogueHudFadeMs, DEFAULT_ANIMATION_TUNING.epilogueHudFadeMs),
    epilogueMoveEasing: stringOr(input.epilogueMoveEasing, DEFAULT_ANIMATION_TUNING.epilogueMoveEasing),
    mobileZoomTweenMs: numberOr(input.mobileZoomTweenMs, DEFAULT_ANIMATION_TUNING.mobileZoomTweenMs),
    mobileZoomTweenEasing: stringOr(input.mobileZoomTweenEasing, DEFAULT_ANIMATION_TUNING.mobileZoomTweenEasing),
    mobileSheetMorphMs: numberOr(input.mobileSheetMorphMs, DEFAULT_ANIMATION_TUNING.mobileSheetMorphMs),
    laborDotGrowthMs: numberOr(input.laborDotGrowthMs, DEFAULT_ANIMATION_TUNING.laborDotGrowthMs),
    laborDotEasing: stringOr(input.laborDotEasing, DEFAULT_ANIMATION_TUNING.laborDotEasing),
    laborDotHeightMs: numberOr(input.laborDotHeightMs, DEFAULT_ANIMATION_TUNING.laborDotHeightMs),
    laborLegendTickerMs: numberOr(input.laborLegendTickerMs, DEFAULT_ANIMATION_TUNING.laborLegendTickerMs),
    laborLegendTickerEasing: stringOr(input.laborLegendTickerEasing, DEFAULT_ANIMATION_TUNING.laborLegendTickerEasing),
  }
}

// ── Easing resolution ─────────────────────────────────────────────────────

// Named JS curves that aren't CSS keywords, expressed as their closest
// cubic-bezier so the same spec can also drive a CSS transition.
const NAMED_EASING_BEZIERS: Record<string, [number, number, number, number]> = {
  easeOutCubic: [0.215, 0.61, 0.355, 1],
  easeInOutCubic: [0.645, 0.045, 0.355, 1],
  easeInOutSine: [0.445, 0.05, 0.55, 0.95],
  linear: [0, 0, 1, 1],
  ease: [0.25, 0.1, 0.25, 1],
  "ease-in": [0.42, 0, 1, 1],
  "ease-out": [0, 0, 0.58, 1],
  "ease-in-out": [0.42, 0, 0.58, 1],
}

const parseCubicBezier = (
  spec: string
): [number, number, number, number] | null => {
  const match = spec
    .trim()
    .match(/^cubic-bezier\(\s*([\d.+-]+)\s*,\s*([\d.+-]+)\s*,\s*([\d.+-]+)\s*,\s*([\d.+-]+)\s*\)$/i)
  if (!match) return null
  const nums = match.slice(1, 5).map(Number)
  return nums.every(Number.isFinite)
    ? (nums as [number, number, number, number])
    : null
}

// A CSS-valid <timing-function> for the spec. cubic-bezier(...) and the CSS
// keywords pass through; a named JS curve maps to its bezier equivalent.
export const resolveCssEasing = (spec: EasingSpec): string => {
  const trimmed = spec.trim()
  if (parseCubicBezier(trimmed)) return trimmed
  if (["linear", "ease", "ease-in", "ease-out", "ease-in-out"].includes(trimmed)) {
    return trimmed
  }
  const named = NAMED_EASING_BEZIERS[trimmed]
  return named ? `cubic-bezier(${named.join(", ")})` : "ease"
}

// Solve a cubic-bezier(x1, y1, x2, y2) easing for y given x∈[0,1], the same
// Newton-Raphson-then-bisection approach browsers use.
const makeCubicBezier =
  (x1: number, y1: number, x2: number, y2: number) =>
  (x: number): number => {
    if (x1 === y1 && x2 === y2) return x // linear
    const ax = 3 * x1 - 3 * x2 + 1
    const bx = 3 * x2 - 6 * x1
    const cx = 3 * x1
    const sampleX = (t: number) => ((ax * t + bx) * t + cx) * t
    const sampleDerivativeX = (t: number) => (3 * ax * t + 2 * bx) * t + cx
    const ay = 3 * y1 - 3 * y2 + 1
    const by = 3 * y2 - 6 * y1
    const cy = 3 * y1
    const sampleY = (t: number) => ((ay * t + by) * t + cy) * t

    let t = x
    for (let i = 0; i < 8; i++) {
      const xEstimate = sampleX(t) - x
      if (Math.abs(xEstimate) < 1e-6) return sampleY(t)
      const derivative = sampleDerivativeX(t)
      if (Math.abs(derivative) < 1e-6) break
      t -= xEstimate / derivative
    }
    let lower = 0
    let upper = 1
    t = x
    while (lower < upper) {
      const xEstimate = sampleX(t)
      if (Math.abs(xEstimate - x) < 1e-6) return sampleY(t)
      if (x > xEstimate) lower = t
      else upper = t
      t = (upper + lower) / 2
    }
    return sampleY(t)
  }

// CSS <timing-function> for the lineMorphEasing curve (the rAF-side mapping
// lives in easeTimedGraphPointProgress), so CSS transitions that must move in
// step with a trajectory morph — e.g. the choose-your-path arrow highlights
// tracking the red loss-of-control overlay — follow the same S-curve.
export const resolveLineMorphCssEasing = (
  easing: AnimationTuning["lineMorphEasing"]
): string =>
  resolveCssEasing(easing === "sine" ? "easeInOutSine" : "easeInOutCubic")

// A JS easing function t∈[0,1] → eased∈[0,1] for the spec (drives rAF tweens).
export const resolveJsEasing = (spec: EasingSpec): ((t: number) => number) => {
  const bezier = parseCubicBezier(spec) ?? NAMED_EASING_BEZIERS[spec.trim()]
  if (bezier) return makeCubicBezier(...bezier)
  return (t: number) => t // unknown → linear
}

// The four bezier control points for the spec, for motion/react's `ease`
// prop (which takes a [x1, y1, x2, y2] array, not a cubic-bezier() string).
// [0, 0, 1, 1] is linear.
export const resolveEasingBezierArray = (
  spec: EasingSpec
): [number, number, number, number] =>
  parseCubicBezier(spec) ?? NAMED_EASING_BEZIERS[spec.trim()] ?? [0, 0, 1, 1]
